import * as React from "react";
import { Label } from "@/dentlab/src/components/ui/label";
import { cn } from "@/dentlab/src/lib/util";
import { HelpCircle } from "lucide-react";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/dentlab/src/components/ui/tooltip";
/** Background that makes a control with a validationError stand out in a dense form. */
export const INVALID_BACKGROUND_CLASS = "bg-red-100";

/** Styling for a control whose InputWrapper shows a validationError. */
export const INVALID_CONTROL_CLASS = `border-destructive ${INVALID_BACKGROUND_CLASS} focus-visible:ring-destructive`;

export interface InputWrapperProps
	extends React.InputHTMLAttributes<HTMLInputElement> {
	validationError?: string | null;
	customHelperText?: string;
	name?: string;
	label?: string;
	required?: boolean;
	inlineLabel?: boolean;
}
export const InputWrapper = React.memo<InputWrapperProps>(
	({
		validationError,
		customHelperText,
		label,
		required,
		className,
		children,
		inlineLabel = false,
		...rest
	}) => {
		const tooltipContent = React.useMemo(() => {
			if (!customHelperText) return null;
			return (
				<Tooltip>
					<TooltipTrigger asChild>
						<HelpCircle className="h-3.5 w-3.5 text-muted-foreground" />
					</TooltipTrigger>
					<TooltipContent>
						<p>{customHelperText}</p>
					</TooltipContent>
				</Tooltip>
			);
		}, [customHelperText]);

		const labelElement = label ? (
			<Label
				className={cn(
					"font-medium flex flex-row items-center gap-1 shrink-0",
					inlineLabel ? "text-xs w-[80px]" : "text-sm"
				)}
			>
				{required ? (
					<span className="after:content-['*'] after:ml-0.5 after:text-destructive">
						{label}
					</span>
				) : (
					label
				)}
				{tooltipContent}
			</Label>
		) : null;

		if (inlineLabel) {
			return (
				<div className={cn("w-full", className)}>
					<div className="flex items-center gap-2">
						{labelElement}
						<div className="relative flex-1 min-w-0">
							{children}
						</div>
					</div>
					{validationError && (
						<p className="text-sm text-destructive">
							{validationError}
						</p>
					)}
				</div>
			);
		}

		return (
			<div className={cn("w-full", className)}>
				{labelElement}
				<div className="relative">{children}</div>
				{(validationError || rest.placeholder) && (
					<p
						className={cn(
							"text-sm",
							validationError
								? "text-destructive"
								: "text-muted-foreground"
						)}
					>
						{validationError || rest.placeholder}
					</p>
				)}
			</div>
		);
	}
);
